import { useState, useEffect, useRef } from 'react';
import { ChevronDown, ArrowDown, Check, Calendar, Clock, MessageSquare, Heart, Users } from 'lucide-react';
import { fbqTrack } from '@/lib/metaPixel';

const WEEKLY_TOPICS = [
  { week: 1, topic: "Outgrowing your environment / Why success still feels heavy" },
  { week: 2, topic: "What you're carrying / Overextension vs burnout" },
  { week: 3, topic: "When performance stops working / The cost of being strong" },
  { week: 4, topic: "Visibility vs value / Being in transition" },
  { week: 5, topic: "Letting go of what isn't yours / Disconnection from self" },
  { week: 6, topic: "Outgrowing people + places / Emotional labor" },
  { week: 7, topic: "Why you're unseen / You need support, not discipline" },
  { week: 8, topic: "Staying too long / Releasing shame" },
  { week: 9, topic: "Letting go of overperformance / Identity shifts" },
  { week: 10, topic: "Freedom / Your next chapter" },
];

const THIS_IS_FOR_YOU = [
  "You're successful... but something still feels off",
  "You're carrying more than your share \u2014 and people expect it",
  "You don't fully know what you want anymore",
  "You're performing at a high level... but not advancing",
  "You feel unseen, blocked, or underleveraged",
  "You're in a season of transition \u2014 but unclear what's next",
  "You're strong for everyone else... but unsupported yourself",
];

const TESTIMONIALS = [
  "I didn't realize how much I was carrying\u2026 until I heard another woman say exactly what I was feeling.",
  "This felt like the first space where I didn't have to perform.",
  "I came in tired. I left clear.",
];

const ResetRoomPage = () => {
  const [showSticky, setShowSticky] = useState(false);
  const calendarRef = useRef(null);

  useEffect(() => {
    // Meta Pixel `init` + `PageView` is fired site-wide from public/index.html.
    // On THIS page (per Crystal's snippet), also fire a `Lead` event on load.
    // Defer slightly so the site-wide pixel script has loaded.
    const t = setTimeout(() => {
      fbqTrack('Lead');
    }, 200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const cal = calendarRef.current;
      if (!cal) { setShowSticky(window.scrollY > 600); return; }
      const rect = cal.getBoundingClientRect();
      setShowSticky(rect.top > window.innerHeight || rect.bottom < 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    // Load Calendly widget script
    const existing = document.querySelector('script[src*="calendly.com/assets/external/widget.js"]');
    if (!existing) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const scrollToCalendar = () => {
    document.getElementById('reserve-seat')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div style={{ fontFamily: "'Manrope', sans-serif", color: '#1A3C34', background: '#F9F8F6' }}>

      {/* ── HERO ── */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" style={{ background: 'linear-gradient(135deg, #1A3C34 0%, #2C5E52 100%)' }}>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-80 h-80 rounded-full blur-3xl" style={{ backgroundColor: '#C06C54' }} />
          <div className="absolute bottom-10 right-20 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: '#E8DCCA' }} />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 relative z-10 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Copy */}
            <div className="text-left">
              <p className="text-sm tracking-[4px] uppercase mb-8" style={{ color: '#C06C54' }}>
                The Reset Room&trade;
              </p>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                You&rsquo;ve been holding everything together&hellip;<br />
                <span style={{ color: '#E8DCCA' }}>but something still feels off.</span>
              </h1>

              <div className="space-y-5 mb-8">
                <p className="text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,0.8)' }}>
                  You&rsquo;ve done everything right. Built the career. Showed up. Delivered. Led.
                </p>
                <p className="text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  And yet&hellip; there&rsquo;s a weight you can&rsquo;t quite explain.
                </p>
              </div>

              <div className="w-12 h-px my-6" style={{ backgroundColor: '#C06C54' }} />

              <p className="text-2xl sm:text-3xl font-bold text-white italic mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                This is your space to set it down.
              </p>

              <p className="text-base leading-relaxed mb-6" style={{ color: '#E8DCCA' }}>
                The Reset Room&trade; is a live, guided experience for high-achieving women who are ready to pause, reflect, and finally hear themselves again.
              </p>
              <div className="flex flex-wrap gap-4 text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
                <span>Not a webinar.</span>
                <span>&bull;</span>
                <span>Not a masterclass.</span>
                <span>&bull;</span>
                <span className="font-semibold text-white">A room.</span>
              </div>

              <button
                onClick={scrollToCalendar}
                className="mt-8 inline-flex items-center gap-2 px-8 py-4 text-white rounded-full text-base tracking-wider uppercase transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5 shadow-lg lg:hidden"
                style={{ backgroundColor: '#C06C54' }}
                data-testid="hero-reserve-btn"
              >
                Reserve Your Seat
              </button>
            </div>

            {/* Right side - Calendly Widget */}
            <div className="hidden lg:block">
              <div className="rounded-2xl overflow-hidden shadow-2xl" style={{ backgroundColor: '#fff' }}>
                <div
                  className="calendly-inline-widget"
                  data-url="https://calendly.com/crystal-and-dr-nicole/the-reset-room"
                  style={{ minWidth: '320px', height: '580px' }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-5 h-5 text-white/30" />
        </div>
      </section>

      {/* ── THIS IS FOR YOU IF... ── */}
      <section className="py-20 px-6" style={{ backgroundColor: '#F9F8F6' }}>
        <div className="max-w-2xl mx-auto">
          <p className="text-sm uppercase tracking-widest text-center mb-3" style={{ color: '#C06C54' }}>
            This is for you if&hellip;
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A3C34' }}>
            You don&rsquo;t need more information.
          </h2>
          <p className="text-xl text-center mb-12 leading-relaxed" style={{ color: '#4A6D62' }}>
            You need a moment of truth.
          </p>

          <p className="text-sm uppercase tracking-widest mb-6 font-semibold" style={{ color: '#C06C54' }}>
            This space was created for you if:
          </p>
          <div className="space-y-4">
            {THIS_IS_FOR_YOU.map((item, i) => (
              <div key={i} className="flex items-start gap-3" style={{ color: '#1A3C34' }}>
                <div className="w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5" style={{ borderColor: '#C06C54' }}>
                  <Check className="w-3 h-3" style={{ color: '#C06C54' }} />
                </div>
                <p className="text-lg leading-relaxed">{item}</p>
              </div>
            ))}
          </div>

          <div className="w-12 h-px mx-auto my-12" style={{ backgroundColor: '#C06C54' }} />

          <div className="text-center">
            <p className="text-xl italic leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '24px', color: '#4A6D62' }}>
              You are not the only one.<br />
              And you don&rsquo;t have to figure this out alone.
            </p>
          </div>
        </div>
      </section>

      {/* ── WHAT HAPPENS IN THE RESET ROOM ── */}
      <section className="py-16 px-6" style={{ backgroundColor: '#1A3C34' }}>
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-6" style={{ backgroundColor: 'rgba(192,108,84,0.15)' }}>
            <Heart className="w-7 h-7" style={{ color: '#C06C54' }} />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            What Happens in the Reset Room&trade;
          </h2>
          <p className="text-lg mb-10" style={{ color: '#E8DCCA' }}>
            This is a guided conversation + sister circle experience led by <strong className="text-white">Crystal Khalil</strong> &amp; <strong className="text-white">Dr. Nicole LaBeach</strong>.
          </p>

          <p className="text-sm uppercase tracking-widest mb-8 font-semibold" style={{ color: '#C06C54' }}>
            Every session is designed to help you:
          </p>
          <div className="grid sm:grid-cols-2 gap-4 max-w-xl mx-auto text-left">
            {[
              "See what you haven't had space to see",
              "Release what you've been carrying",
              "Hear your own truth clearly",
              "Connect with women who understand without explanation",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                <span className="text-lg mt-0.5" style={{ color: '#C06C54' }}>&rarr;</span>
                <p className="text-white text-base leading-relaxed">{item}</p>
              </div>
            ))}
          </div>

          <div className="w-12 h-px mx-auto my-10" style={{ backgroundColor: '#C06C54' }} />
          <div className="space-y-2" style={{ color: 'rgba(232,220,202,0.7)' }}>
            <p className="text-lg">You can listen. You can reflect. You can share.</p>
            <p className="text-xl font-semibold text-white italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              There is no pressure here.
            </p>
          </div>
        </div>
      </section>

      {/* ── LIVE SESSIONS ── */}
      <section className="py-20 px-6" style={{ backgroundColor: '#F9F8F6' }}>
        <div className="max-w-3xl mx-auto">
          <p className="text-sm uppercase tracking-widest text-center mb-3" style={{ color: '#C06C54' }}>
            Live Sessions
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A3C34' }}>
            Choose Your Room
          </h2>
          <p className="text-lg text-center mb-12" style={{ color: '#4A6D62' }}>
            We meet twice a week &mdash; choose the space that meets you where you are:
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Wednesday */}
            <div className="rounded-2xl overflow-hidden border hover:shadow-lg transition-all duration-300" style={{ borderColor: '#E8DCCA', backgroundColor: '#fff' }}>
              <div className="p-1" style={{ backgroundColor: '#C06C54' }} />
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-5 h-5" style={{ color: '#C06C54' }} />
                  <span className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#C06C54' }}>Wednesday</span>
                </div>
                <div className="flex items-center gap-2 mb-6">
                  <Clock className="w-4 h-4" style={{ color: '#4A6D62' }} />
                  <span className="text-lg font-bold" style={{ color: '#1A3C34' }}>12:00 PM ET</span>
                </div>
                <p className="text-base leading-relaxed italic" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', color: '#4A6D62' }}>
                  For the woman navigating career, leadership, and visibility
                </p>
              </div>
            </div>

            {/* Thursday */}
            <div className="rounded-2xl overflow-hidden border hover:shadow-lg transition-all duration-300" style={{ borderColor: '#E8DCCA', backgroundColor: '#fff' }}>
              <div className="p-1" style={{ backgroundColor: '#1A3C34' }} />
              <div className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-5 h-5" style={{ color: '#1A3C34' }} />
                  <span className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#1A3C34' }}>Thursday</span>
                </div>
                <div className="flex items-center gap-2 mb-6">
                  <Clock className="w-4 h-4" style={{ color: '#4A6D62' }} />
                  <span className="text-lg font-bold" style={{ color: '#1A3C34' }}>7:00 PM ET</span>
                </div>
                <p className="text-base leading-relaxed italic" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', color: '#4A6D62' }}>
                  For the woman processing, releasing, and reconnecting with herself
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 10-WEEK EXPERIENCE ── */}
      <section className="py-16 px-6" style={{ backgroundColor: '#1A3C34' }}>
        <div className="max-w-3xl mx-auto">
          <p className="text-sm uppercase tracking-widest text-center mb-3" style={{ color: '#C06C54' }}>
            Current 10-Week Experience
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-center text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Each week, we hold space around a different truth
          </h2>
          <p className="text-base text-center mb-12" style={{ color: '#E8DCCA' }}>
            many high-achieving women are silently carrying:
          </p>

          <div className="space-y-3">
            {WEEKLY_TOPICS.map((item) => (
              <div key={item.week} className="flex items-start gap-4 p-4 rounded-xl transition-all duration-300 hover:bg-white/5" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(192,108,84,0.15)' }}>
                  <span className="text-sm font-bold" style={{ color: '#C06C54' }}>{item.week}</span>
                </div>
                <p className="text-base leading-relaxed text-white">{item.topic}</p>
              </div>
            ))}
          </div>

          <div className="w-12 h-px mx-auto my-10" style={{ backgroundColor: '#C06C54' }} />
          <div className="text-center">
            <p className="text-lg" style={{ color: 'rgba(232,220,202,0.7)' }}>
              You don&rsquo;t need to attend all 10 weeks.
            </p>
            <p className="text-xl font-semibold text-white mt-2 italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              You just need to start.
            </p>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-20 px-6" style={{ backgroundColor: '#E8DCCA' }}>
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mx-auto mb-8 block" style={{ backgroundColor: 'rgba(26,60,52,0.1)' }}>
            <MessageSquare className="w-7 h-7" style={{ color: '#1A3C34' }} />
          </div>
          <p className="text-sm uppercase tracking-widest text-center mb-3" style={{ color: '#C06C54' }}>
            What women experience
          </p>
          <div className="space-y-8 mt-10">
            {TESTIMONIALS.map((quote, i) => (
              <blockquote key={i} className="text-center">
                <p className="text-2xl sm:text-3xl font-bold italic leading-snug" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A3C34' }}>
                  &ldquo;{quote}&rdquo;
                </p>
                {i < TESTIMONIALS.length - 1 && (
                  <div className="w-8 h-px mx-auto mt-8" style={{ backgroundColor: '#C06C54' }} />
                )}
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT MAKES THIS DIFFERENT ── */}
      <section className="py-16 px-6" style={{ backgroundColor: '#F9F8F6' }}>
        <div className="max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-6" style={{ backgroundColor: 'rgba(192,108,84,0.1)' }}>
            <Users className="w-7 h-7" style={{ color: '#C06C54' }} />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-8" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A3C34' }}>
            What Makes This Different
          </h2>
          <div className="space-y-5 text-xl leading-relaxed" style={{ color: '#4A6D62' }}>
            <p>This is not about doing more.</p>
            <p className="font-semibold" style={{ color: '#1A3C34' }}>This is about being supported differently.</p>
          </div>
          <div className="w-12 h-px mx-auto my-10" style={{ backgroundColor: '#C06C54' }} />
          <div className="space-y-4 text-lg leading-relaxed" style={{ color: '#4A6D62' }}>
            <p>You are not being taught at.</p>
            <p className="font-semibold text-xl" style={{ color: '#1A3C34' }}>
              You are being seen, reflected, and held.
            </p>
            <p className="italic mt-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '24px', color: '#C06C54' }}>
              And that changes everything.
            </p>
          </div>
        </div>
      </section>

      {/* ── RESERVE YOUR SEAT (Calendly) ── */}
      <section id="reserve-seat" ref={calendarRef} className="py-20 px-6" style={{ backgroundColor: '#1A3C34' }}>
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm uppercase tracking-widest mb-3" style={{ color: '#C06C54' }}>
            Reserve Your Seat
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Choose the session that feels right for you.
          </h2>
          <div className="space-y-2 mb-10" style={{ color: '#E8DCCA' }}>
            <p className="text-lg">This is your time to pause.</p>
            <p className="text-lg">To reset.</p>
            <p className="text-xl font-semibold text-white italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              To hear yourself again.
            </p>
          </div>

          {/* Calendly Embed */}
          <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#fff' }}>
            <div
              className="calendly-inline-widget"
              data-url="https://calendly.com/crystal-and-dr-nicole/the-reset-room"
              style={{ minWidth: '320px', height: '700px' }}
            />
          </div>
        </div>
      </section>

      {/* ── IF YOU KNOW YOU NEED MORE ── */}
      <section className="py-16 px-6" style={{ backgroundColor: '#E8DCCA' }}>
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-8 h-px mx-auto mb-8" style={{ backgroundColor: '#C06C54' }} />
          <h3 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A3C34' }}>
            If you know you need more
          </h3>
          <div className="space-y-4 text-lg leading-relaxed" style={{ color: '#4A6D62' }}>
            <p>For some women, this is the beginning.</p>
            <p>For others&hellip; you already know this goes deeper.</p>
          </div>
          <div className="w-12 h-px mx-auto my-8" style={{ backgroundColor: '#C06C54' }} />
          <p className="text-xl italic mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', color: '#1A3C34' }}>
            &ldquo;I don&rsquo;t just need a conversation&hellip; I need space to truly reset&rdquo;
          </p>
          <div className="space-y-3" style={{ color: '#4A6D62' }}>
            <p className="text-lg">
              There is an opportunity to step into a more immersive experience with us.
            </p>
            <p className="text-lg font-semibold" style={{ color: '#1A3C34' }}>
              But for now&hellip; start here.
            </p>
          </div>
          <button
            onClick={scrollToCalendar}
            className="mt-8 inline-flex items-center gap-2 px-8 py-4 text-white rounded-full text-base tracking-wider uppercase transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5 shadow-lg"
            style={{ backgroundColor: '#C06C54' }}
            data-testid="bottom-reserve-btn"
          >
            Reserve Your Seat
          </button>
          <div className="w-8 h-px mx-auto mt-10" style={{ backgroundColor: '#C06C54' }} />
        </div>
      </section>

      {/* ── STICKY MOBILE CTA BAR ── */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ${showSticky ? 'translate-y-0' : 'translate-y-full'}`}
        data-testid="sticky-cta-bar"
      >
        <div className="backdrop-blur-md border-t px-4 py-3 flex items-center justify-between gap-3 max-w-xl mx-auto sm:max-w-none"
          style={{ backgroundColor: 'rgba(26,60,52,0.95)', borderColor: 'rgba(192,108,84,0.3)' }}
        >
          <p className="text-white text-sm font-medium hidden sm:block">
            Your seat is waiting.
          </p>
          <button
            onClick={scrollToCalendar}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 text-white font-semibold px-6 py-2.5 rounded-full text-sm transition-all duration-300 hover:opacity-90 shadow-lg"
            style={{ backgroundColor: '#C06C54' }}
            data-testid="sticky-reserve-btn"
          >
            Reserve Your Seat
            <ArrowDown className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetRoomPage;
